/**
 * `combatInspector` — the **pure models** behind COMBAT.app's combat-resolution windows. The
 * draggable window *bodies* live in {@link ./CombatWindows.tsx}; this is the host-agnostic,
 * unit-testable core they lean on:
 *
 *   - the {@link CombatWindowSpec}s — the singleton RESOLUTION window + the per-unit inspector spec
 *     factory ({@link inspectorSpec}, keyed `inspector:<combatantId>`, opened by double-clicking a
 *     token), on explicit GM action only (no auto-open). Turn state is **not** a window: it is
 *     drawn on the tokens and in the round header (`decisions/0190`), since the round, the active
 *     side and who has gone are state the GM watches every moment, not a panel to summon;
 *   - {@link ActionDraft}, the armed Skirmish/Activate aim the inspector's action bar holds
 *     and the canvas consumes by **clicking a target token**;
 *   - {@link aimResolvesToFire}, the pure decision a token-click runs: with an action armed,
 *     a click on a *different* combatant fires the action at it instead of re-selecting;
 *   - {@link armedCellSightlines}, the **per-cell targeting advisory** — one
 *     {@link CellSightline} (targetable + cover) per reach-wash cell, straight from the attacker's
 *     footprint to that cell, not per candidate combatant. `CombatStage`'s reach-wash layer draws
 *     from this: only targetable cells get a dot, and among those, covered cells get an extra
 *     treatment.
 */
import type { Combatant, CombatantId, EncounterState, ReachCell, WeaponProfile, WeaponRange } from "@lancer-vtt/domain";
import { attackReachKeys, combatantInReachKeys, hasStatus, HIDDEN_STATUS, isRangedAttackAt, sideOf, sightlinesFrom, terrainVisibility, worldFor } from "@lancer-vtt/domain";
import {
  alliedCoverCells,
  checkJockeyLegality,
  checkMountLegality,
  checkRamSize,
  checkRideLegality,
  combatantRange,
  immuneToTechAction,
  quickTechOption,
  type DeployPlacement,
  techMenuPick,
  type DirectedDeployRequirement,
  type LoadedOverlay,
} from "@lancer-vtt/rules";
import type { WireCell } from "@lancer-vtt/shared";
import type { Cover } from "@lancer-vtt/space";
import type { CombatWindowSpec } from "./windows";

// ── per-unit inspector windows ──────────────────────────────────────────────────────────────
//
// Inspectors are **dynamic, per-unit** windows opened by double-clicking a token in EDIT (never one
// selection-bound singleton): window id `inspector:<combatantId>` within the EDIT desktop. PLAY has
// no inspector window: a unit's sheet there is the unit panel, expanded (`UnitColumn.tsx`,
// `decisions/0200`). The per-mode registry already keys by arbitrary string id and survives a
// close/reopen, so the host mints a spec per unit.

/** The window-id prefix shared by every per-unit inspector (`inspector:<combatantId>`). */
export const INSPECTOR_ID_PREFIX = "inspector:";

/** The window id for a given combatant's inspector. */
export function inspectorWindowId(combatantId: CombatantId): string {
  return `${INSPECTOR_ID_PREFIX}${combatantId}`;
}

/** The combatant id an inspector window id addresses, or `null` when the id isn't an inspector's. */
export function inspectorCombatantId(windowId: string): string | null {
  return windowId.startsWith(INSPECTOR_ID_PREFIX) ? windowId.slice(INSPECTOR_ID_PREFIX.length) : null;
}

/**
 * Mint a per-unit inspector {@link CombatWindowSpec} for a combatant. `index` staggers the *first*
 * open's default geometry (so a second/third inspector doesn't land exactly atop the first); the
 * registry seeds geometry only on first open, so a reopen keeps the GM's last placement regardless
 * of `index`. Keyed `inspector:<combatantId>`, so re-opening the same unit dedupes and focuses.
 *
 * `opts.kind` picks the *default* size: 500×560 for a mech, an NPC or a pilot, 440×560 for drones,
 * deployables and objects. The window is EDIT's (`EditInspectorBody`), so these sizes are EDIT's
 * alone (`decisions/0090`). At 1280 the lane clamps it to its 360px floor (`windowLane.ts`,
 * `decisions/0191`). Only the first-open default is set here; the viewer's resized geometry persists.
 */
export function inspectorSpec(combatantId: CombatantId, index = 0, opts?: { kind?: Combatant["kind"] }): CombatWindowSpec {
  const STAGGER = 28;
  const unitSheet = { w: 500, h: 560 };
  const generic = { w: 440, h: 560 };
  const kind = opts?.kind;
  return {
    id: inspectorWindowId(combatantId),
    defaultPos: { x: index * STAGGER, y: 88 + index * STAGGER },
    defaultSize: kind === "mech" || kind === "npc" || kind === "pilot" ? unitSheet : generic,
  };
}

// ── per-room inspector windows ──────────────────────────────────────────────────────────────
//
// A room inspector is the room twin of the per-unit inspector above: a dynamic GMS window opened by
// **double-clicking a rendered room's glowing floor**, keyed `room-inspector:<roomId>` within the
// active desktop. It mirrors the unit inspector's lifecycle exactly (mint a spec per id, dedupe +
// raise on reopen, stagger only the first open's geometry). The body holds a rename input, a
// delete-room action, and a per-rect ✕ — all routed through the EXISTING `set_room` / `remove_room`
// commands (no new command), so every edit is synced + undoable.

/** The window-id prefix shared by every per-room inspector (`room-inspector:<roomId>`). */
export const ROOM_INSPECTOR_ID_PREFIX = "room-inspector:";

/** The window id for a given room's inspector. */
export function roomInspectorWindowId(roomId: string): string {
  return `${ROOM_INSPECTOR_ID_PREFIX}${roomId}`;
}

/** The room id an inspector window id addresses, or `null` when the id isn't a room inspector's. */
export function roomInspectorRoomId(windowId: string): string | null {
  return windowId.startsWith(ROOM_INSPECTOR_ID_PREFIX) ? windowId.slice(ROOM_INSPECTOR_ID_PREFIX.length) : null;
}

/**
 * Mint a per-room inspector {@link CombatWindowSpec}. `index` staggers the *first* open's default
 * geometry; the registry seeds geometry only on first open, so a reopen keeps the GM's last placement.
 * Keyed `room-inspector:<roomId>`, so re-opening the same room dedupes and focuses.
 */
export function roomInspectorSpec(roomId: string, index = 0): CombatWindowSpec {
  const STAGGER = 28;
  return {
    id: roomInspectorWindowId(roomId),
    defaultPos: { x: 40 + index * STAGGER, y: 120 + index * STAGGER },
    defaultSize: { w: 360, h: 360 },
  };
}

/**
 * The **DEPLOY picker** — the transient singleton window the UNITS panel's ADD ENEMY /
 * ADD MECH buttons open, in either mode. It self-closes on a pick (the deployed unit lands
 * UNPLACED). One
 * window, two entry points: the host's picker-kind state selects NPC-class vs
 * roster-mech content. The lighter "＋ add" picker weight, not a full GMS surface.
 */
export const DEPLOY_PICKER_WINDOW: CombatWindowSpec = {
  id: "deploy-picker",
  defaultPos: { x: 400, y: 120 },
  defaultSize: { w: 380, h: 440 },
};

/**
 * The armed combat action a PLAY inspector's action bar holds — the up-front declaration.
 * **There is at most one armed action board-wide**: it carries its own `attackerId` (the combatant
 * firing it) rather than being bound to the current selection, so arming an action in one unit's
 * inspector replaces any other. While set, a click on a *different* combatant's token fires it as the target (skirmish)
 * / binds it (activate); a self-activation fires with no target.
 */
export type ActionDraft =
  | {
      attackerId: CombatantId;
      mode: "skirmish";
      weaponId: string;
      profileId: string;
      accuracy: number;
      /**
       * Arm this as a **GM-declared Overwatch** rather than an ordinary Skirmish — the NPC inspector's Overwatch row sets this `true` when arming; the
       * identical weapon/profile/accuracy pick + board-click-a-target flow fires `request_attack` with
       * `reaction: true` (riding the shipped Overwatch reaction path) instead of a standard attack.
       * Absent/false on every ordinary Skirmish arm (mech or NPC).
       */
      reaction?: boolean;
      /**
       * A **pre-filled, fixed target** — the combatant this shot is locked onto, so no board click is
       * needed to pick one. Set only by the Overwatch move-checkpoint *take* flow
       * (`CombatApp.startOverwatchTake`), where the target is fixed by the trigger — the unit whose
       * declared move tripped the checkpoint — so making the watcher re-target the mover on the board
       * would be a redundant step. When present, the inline
       * `SkirmishChooser` renders an explicit FIRE button that resolves straight against this id (the
       * weapon/profile/accuracy pick stays live); the ordinary board-click-a-target path is unaffected
       * when it's absent (every non-take Skirmish/Overwatch arm).
       */
      fixedTargetId?: CombatantId;
      /**
       * Pre-declared answers, keyed per answer-key family — this ONE map carries two structurally
       * distinct families, and their absence semantics are NOT the same:
       *   - **Ammo `choose` prompt ids** — a bare `promptId` key, the inspector's ammo-selector
       *     fast-path over the live mid-resolve `choose` prompt. Populated only when the player
       *     explicitly picks an option in `SkirmishChooser`'s ammo selector (`CombatWindows.tsx`);
       *     **absent for a given promptId means "ask me"** — dispatch omits that entry and the live
       *     prompt fires instead. Opt-in per shot, no sticky memory.
       *   - **Opt-in rider answers** (`"optIn:" + name`, `OptInRiderPrompt.key`) — the checkbox row
       *     group's explicit `"true"`/`"false"` string per projected `requiresOptIn` clause
       *     (`ProjectedEncounterState.optInRiders`). **Absent means something different from ammo:
       *     "no explicit deviation from the authored default"** (`OptInRiderPrompt.default`), never
       *     "ask me" — there is no live mid-resolve opt-in prompt to fall back to, and the engine is
       *     fail-closed (a missing answer DECLINES). So `CombatApp.tsx`'s `fireAt`
       *     (`resolveSkirmishFireChoices`) resolves EVERY projected rider to an explicit wire value at
       *     fire time regardless of whether the player touched the checkbox, and strips any stale
       *     `optIn:` key left over from a previously-armed weapon. Per-shot only, no sticky state
       *     across shots — same rule as the ammo family.
       * Threaded straight into `buildAttackIntent`'s `AttackDeclaration.choices`, mirroring the
       * `ram_push` precedent (`CombatApp.tsx`'s `fireAt`).
       */
      choices?: Record<string, string>;
      /**
       * The units picked so far for a weapon that **attacks several targets at a time** (the
       * Sharanga Missiles' "two targets", the Missile Pods' "1-3 characters"; the weapon entry's
       * `attacksTargets`): a board click toggles a unit in or out, and the shot fires at the cap or
       * on the banner's CONFIRM, with the first pick as the target and the rest as
       * `RequestAttack.alsoTargets` (`multiTarget.ts`). Absent for every other weapon.
       */
      picks?: CombatantId[];
    }
  | { attackerId: CombatantId; mode: "activate"; actionId: string }
  /**
   * **System-granted tech attack** — a granted row
   * whose {@link ActionDescriptor} carries `route: "attack"` (an equipped system/weapon RAW-fires
   * as a tech attack, e.g. SSC Markerlight) instead of `route: "activate"`. **Not** a variant of
   * `"activate"` above, deliberately: a tech-attack row is ALSO `targeting: "single"`, so without
   * its own mode `fireGranted` would arm the ordinary `"activate"` aim and a board click would send
   * `request_activate` — that intent has no `tech`/`weapon` concept, so the room would silently
   * resolve it as a bare self-activation (or reject it) instead of the intended `request_attack{
   * tech:true, weapon:actionId}`. Carries `accuracy` (Skirmish/Quick-Tech's shape) since a tech
   * attack rolls d20+Tech vs. a defense, unlike a plain `activate` which has no roll. `actionId` is
   * the composite `<itemId>:<actionIndex>` id `techAttackActions` freezes — the SAME id
   * `CombatApp.tsx`'s `fireAt` sends as `RequestAttack.weapon`.
   */
  | { attackerId: CombatantId; mode: "tech_attack"; actionId: string; accuracy: number }
  /**
   * **Activate at a board CELL** — a point-blanket deploy/throw
   * whose overlay reads `for_each origin:"site"` (Smoke/HEX Charges thrown to a point). Armed by the
   * granted deploy/throw row when its {@link ActionDescriptor} `targeting` is `"cell"`; unlike
   * `"activate"` (arm → click a TOKEN → `target`), the next bare-cell click supplies `RequestActivate.site`
   * via the stage's AoE-origin commit path (`CombatApp`'s `onCommitCell`, the same seam an area
   * Skirmish rides). The server stays the wall (placement legality); the CLI's `activate --site x,y`
   * is the scriptable twin.
   */
  | { attackerId: CombatantId; mode: "activate_cell"; actionId: string }
  /**
   * **Directed multi-pick placement** — a `deploy_object` atom's player-chosen cells (Jericho's two
   * mutually-adjacent sections, the turret drone's one), accumulated **client-side** across `count`
   * board-cell clicks before anything is sent. This is the one draft shape that does not commit or
   * decline on **each** board click (see {@link aimResolvesToFire}'s doc — Barrage/Full Tech send a
   * complete intent per click and re-arm, with the server-side `AttackLock` tracking progress;
   * Stabilize/Search collect picks in local React state and are excluded from board firing). This
   * variant grows `picks` in place and fires exactly **one** `request_activate` carrying every cell
   * once `picks.length === count` — nothing is sent until the draft is complete, so
   * cancel-after-one-pick is a pure client-state discard, never a server round-trip to unwind, and
   * there is no `AttackLock` for this gesture at all. `count`/`size` are minted by
   * `directedDeployRequirement` (`@lancer-vtt/rules`) — the ONE place the pick shape is derived,
   * matching the granted row's own `"sites"` classification (`actionRegistry.ts`) and the room
   * gate's count/size check; never re-derive it here. The pure transitions below
   * ({@link appendDirectedDeployPick}, {@link popDirectedDeployPick},
   * {@link directedDeployDraftComplete}) are unit-tested independently of any component, exactly as
   * {@link cellPickBypassesSelect}/{@link aimResolvesToFire} are.
   */
  | {
      attackerId: CombatantId;
      mode: "activate_sites";
      actionId: string;
      /** Total picks required — `directedDeployRequirement(...).count` (Jericho 2, the turret drone 1). */
      count: number;
      /** Each section's footprint size — `directedDeployRequirement(...).size`, the wash's input. */
      size: number;
      /**
       * True iff this draft came from a `deploy_mine` atom — `directedDeployRequirement(...).mine`,
       * carried through so the wash memo below can pass it back into `legalDirectedDeployCells`.
       * Absent for every non-mine directed deploy (Jericho's two
       * cover sections, the turret drone), matching `DirectedDeployRequirement.mine`'s own
       * optional/absent-for-non-mine shape — never re-derive it from `actionId`.
       */
      mine?: boolean;
      /**
       * `"sensors"` for a drone deploy — `directedDeployRequirement(...).placement`, carried through
       * so the wash memo offers the Sensors-and-line-of-sight set instead of adjacency. Absent for
       * every adjacency deploy.
       */
      placement?: DeployPlacement;
      /**
       * The cells picked so far, **in order** — grows by one per {@link appendDirectedDeployPick}
       * call, shrinks by one per {@link popDirectedDeployPick} (the "back" affordance). Threaded
       * onto `RequestActivate.sites` verbatim, in this same order, on the final pick
       * ({@link buildActivateIntent}'s `ActivateDeclaration.sites`).
       */
      picks: WireCell[];
    }
  | {
      attackerId: CombatantId;
      mode: "barrage";
      weaponId: string;
      profileId: string;
      accuracy: number;
      /**
       * The committed mount keys (`RequestBarrage.mounts`, R7): chosen before the first shot (the
       * command menu's mount card, the sheet chooser's mount toggles) and sent on every shot.
       */
      mounts?: string[];
      /** This shot's picked units for a weapon that attacks several targets at a time (the Skirmish draft's `picks`). */
      picks?: CombatantId[];
    }
  | { attackerId: CombatantId; mode: "ram"; push: boolean }
  | { attackerId: CombatantId; mode: "grapple" }
  | { attackerId: CombatantId; mode: "improvised_attack" }
  | { attackerId: CombatantId; mode: "stabilize" }
  /**
   * Self-Destruct — arming expands the in-panel
   * `SelfDestructChooser` (a self-targeted `at ∈ {1,2,3}` pick, Stabilize's exact no-board-target
   * shape). No up-front declaration lives on the draft itself — the chooser holds the pick locally
   * and fires `buildSelfDestructIntent` on FIRE, mirroring Stabilize.
   */
  | { attackerId: CombatantId; mode: "self_destruct" }
  /**
   * **Dismount / Eject** (RAW Core p.70) — the mech↔pilot bridge, the `activate_cell` shape exactly
   * (arm → click a bare/occupied board CELL → commit `{ actor, at }`), not a token-fire gesture:
   * Dismount's landing cell must be adjacent, Eject's anywhere within fly 6 (both mirrored by the
   * `attackRange` wash off the real exported wall predicates — `checkDismountLegality`/
   * `checkEjectLegality`, never a hand-rolled distance check). No up-front declaration beyond the
   * cell itself — unlike a granted `activate_cell` throw, these are hardcoded CORE mech actions
   * (`MECH_ACTIONS`'s `dismount`/`eject` rows), so the draft carries no `actionId` to look up.
   */
  | { attackerId: CombatantId; mode: "dismount" }
  | { attackerId: CombatantId; mode: "eject" }
  /**
   * **Dismount (ally)** / `ride_dismount` (RAW Core p.70) — the riding rider's own
   * explicit climb-off, Dismount/Eject's exact CELL-pick shape (arm → click a bare/occupied
   * board CELL → commit `{ actor, at }`), **not** Ride's unit-pick shape: the carrier is read
   * server-side off the rider's own `Riding.partner` (never named on the wire), but the landing
   * space is player-chosen, so it still needs a board click. Mirrored by the `attackRange` wash
   * off the real exported `checkRideDismountLegality` (`@lancer-vtt/rules`), never a hand-rolled
   * adjacency check. No up-front declaration beyond the cell itself.
   */
  | { attackerId: CombatantId; mode: "ride_dismount" }
  | {
      attackerId: CombatantId;
      mode: "quick_tech";
      /**
       * Which Quick Tech option is armed — one of `quickTechOption`'s ids (`"scan" | "lock_on" |
       * "invade" | "bolster"`), or `""` before the player picks one — mirrors Skirmish/Barrage's
       * `weaponId` empty-string "unarmed" sentinel, kept a plain `string` rather than a literal union
       * since `quickTechOption` is the authoritative resolver of an id's validity (the same shape
       * `techTargetReason`'s `TechTargetArgs.optionId` uses).
       */
      optionId: string;
      /**
       * Net accuracy (+) / difficulty (−) for the Invade tech-attack route; ignored by the three
       * `activate` routes (Scan/Lock On/Bolster). Always present (defaults 0), mirroring Skirmish's
       * always-present `accuracy`.
       */
      accuracy: number;
    }
  | {
      attackerId: CombatantId;
      mode: "full_tech";
      /**
       * Which Quick Tech option is armed for the NEXT pick — same `""`/id sentinel shape as
       * `quick_tech`'s `optionId`. The wire carries one pick per `request_full_tech`, so the client
       * aim only ever holds ONE in-flight pick at a time — how many picks have already fired lives
       * server-side on the `AttackLock`, not on this draft. Cleared back to `""` after each pick fires (the chooser
       * re-arms for pick 2 rather than the aim being cleared, mirroring Barrage's re-arm).
       */
      optionId: string;
      /** Net accuracy (+) / difficulty (−) for the Invade tech-attack route; ignored otherwise. */
      accuracy: number;
      /**
       * The picks still to fire after `optionId`, when the command menu armed both up front
       * ("Run both", `projects/token-command-menu.md`). `fireAt`'s re-arm keeps it (it spreads the
       * aim), and {@link advanceFullTechQueue} arms the next pick, or clears the aim once none is
       * left. Absent on the sheet's chooser, which re-arms by hand.
       */
      queue?: string[];
    }
  | { attackerId: CombatantId; mode: "search" }
  /**
   * **Disarm Mine** (Core p.106) — a unit pick, Search's shape: the command menu's Disarm Mine card
   * lists the mines this unit may disarm (`checkMineDisarmLegality`, `@lancer-vtt/rules`) and fires
   * on its own button. The draft holds no pick; a token click while it is armed selects.
   */
  | { attackerId: CombatantId; mode: "disarm_mine" }
  /**
   * **Mount** (RAW Core p.70) — the on-foot pilot's own **Full** action, climbing back into its own
   * adjacent Unpiloted mech. Unlike Dismount/Eject (the mech's own actions, a board-CELL commit),
   * Mount targets a MECH **by id** — a unit pick, exactly Search's shape: the menu card's
   * `MountControls` filters `state.order` to this pilot's own adjacent Unpiloted mech(s) (off the
   * real exported `checkMountLegality`, `@lancer-vtt/rules`, never a hand-rolled adjacency/
   * ownership check) and fires `buildMountIntent` on MOUNT. No up-front declaration lives on the
   * draft itself — the chooser holds the pick locally, mirroring `search`'s own shape exactly.
   */
  | { attackerId: CombatantId; mode: "mount" }
  /**
   * **Jockey** (RAW Core p.75) — the on-foot pilot's own **Full** action, a contested-GRIT
   * attach attempt against an adjacent HOSTILE mech. **A unit pick, Mount's exact shape** (never
   * `legalPilotBridgeCells` — that is Dismount/Eject's board-CELL shape): the
   * menu card's `JockeyControls` filters `state.order` to adjacent hostile mech-scale candidates (off
   * the real exported `checkJockeyLegality`, `@lancer-vtt/rules`, via {@link jockeyTargetReason} —
   * never a hand-rolled hostility/adjacency check) and fires `buildJockeyIntent` on JOCKEY. The
   * contest itself (GRIT vs. HULL) is drawn entirely server-side; this draft carries no roll. No
   * up-front declaration lives on the draft itself, mirroring `mount`'s own shape exactly.
   */
  | { attackerId: CombatantId; mode: "jockey" }
  /**
   * **Ride** (RAW Core p.70) — the on-foot pilot's own **Full** action, climbing onto a
   * willing adjacent ALLIED (same-side) mech. Structurally Jockey's exact sibling with the side
   * test inverted and no contest (a willing ally always attaches, no dice): the menu card's
   * `RideControls` filters `state.order` to adjacent same-side mech candidates (off the real
   * exported `checkRideLegality`, `@lancer-vtt/rules`, via {@link rideTargetReason}) and fires
   * `buildRideIntent` on RIDE. No up-front declaration, mirroring `mount`/`jockey`.
   */
  | { attackerId: CombatantId; mode: "ride" }
  /**
   * **Prepare (arm a hold)** — RAW Core pp. 71–72, the composer half of the prepare/release pair
   * below. Armed by the QUICK column's PREPARE row; expands the in-panel `PrepareChooser`, which
   * collects all three declarations the engine's `HeldAction` needs UP FRONT (the Stabilize /
   * Self-Destruct form-plus-commit-button shape, `projects/prepare-hold-browser-arming.md` D1) and
   * commits from its OWN button — a `prepare` **command**, never a board click.
   *
   * **Not a firing mode.** `prepare` is deliberately absent from {@link aimResolvesToFire} (see its
   * explicit early return): a hold has no board target at prepare time at all — RAW's own "ram
   * *them*" example is trigger-determined and unknowable until release — so a token click while the
   * composer is up must fall through to plain selection.
   *
   * Both string fields use Skirmish's `""`-sentinel convention rather than an optional key:
   *   - `readiedActionId` — `""` until a verb row is picked, then a `PREPARABLE_ACTION_IDS` member
   *     (`"skirmish"` / `"ram"` / `"grapple"` / `"quick_tech"`). `"improvised_attack"` is in that
   *     engine set but is a FULL action, so the composer's own `cost === "Quick"` filter excludes
   *     it (D5) — nothing here re-states that rule.
   *   - `weaponId` — `""` unless the readied verb needs a named weapon. For `"skirmish"` it is a
   *     `weaponInstanceKey` (`"<mountIdx>:<slotIdx>"`), the SAME key `SkirmishChooser`'s cards
   *     carry, NOT the compendium id; for `"quick_tech"` the composer pins the literal
   *     `"act_invade"` (the one Invade flavor `isAttackShapedHeld` accepts). Ram/Grapple carry none
   *     and the built `HeldAction` omits `weapon` entirely.
   *
   * `triggerText` is the freeform "when X, then Y" string the engine stores unparsed and
   * `PreparedHoldPanel` renders verbatim (`decisions/0069` ruling 1) — never an X/Y pair of inputs,
   * which would imply the engine evaluates it (D2).
   */
  | { attackerId: CombatantId; mode: "prepare"; readiedActionId: string; weaponId: string; triggerText: string }
  /**
   * **Release a held action** — armed by the PREPARED unit's
   * "RELEASE NOW" control; no up-front declaration (mirrors Grapple/Improvised Attack — the
   * readied verb + weapon are already pinned in `activation.heldActions[attackerId]`, so there
   * is nothing left to pick but the target). The next board click fires `buildReleaseIntent`
   * (`attackIntent.ts`), which derives the weapon/tech pair from the held record and names
   * `releasedBy: attackerId` so the room's `resolveRelease` resolves the hold out of turn
   * instead of a standard attack.
   */
  | { attackerId: CombatantId; mode: "release" };

/**
 * Whether a token click should **fire the armed action** at `clickedId` rather than re-select it.
 * True iff a `skirmish`/`activate`/`ram`/`grapple`/`improvised_attack` action is armed and the
 * click landed on a real combatant other than the armed attacker (the `attackerId` the aim itself
 * carries). The host fires the intent on `true`, else updates selection. **Ram, Grapple, and
 * Improvised Attack target by a canvas click too** (all three are single-target melee actions —
 * Ram's knock-back push is pre-declared on the aim in the chooser, Grapple and Improvised Attack
 * carry no up-front declaration at all — so a click on a valid adjacent target fires it; the
 * host's `outOfRangeTargetIds` legality gate, off {@link ramTargetReason}/{@link grappleTargetReason}/
 * {@link improvisedAttackTargetReason}, keeps an illegal target a select, not a doomed shot).
 * **Barrage is roll-as-you-assign**:
 * it fires from a board click exactly like Skirmish, but only **once a weapon card is armed** (`weaponId
 * !== ""`) — the board click resolves THAT one shot (`request_barrage`), then the weapon auto-disarms for
 * the next card. With no weapon card armed yet, a click while Barrage is up just selects (the in-panel
 * chooser is still picking the weapon). **Quick Tech mirrors Barrage's gate**: the in-panel chooser picks WHICH option (`aim.optionId`, `""` until chosen — the option-row
 * click is the twin of Skirmish's weapon-card click), then a board click fires it at the clicked token
 * via `quickTechIntent`; before an option is armed, a click just selects. **Full Tech mirrors the SAME
 * gate**: the in-panel chooser picks WHICH option for
 * the NEXT pick (`aim.optionId`), a board click fires ONE `request_full_tech` pick at the clicked token,
 * then the chooser re-arms (`optionId` back to `""`) for the second pick rather than the aim clearing —
 * the server's `AttackLock` (not this draft) tracks how many of the two picks have fired. **Search**
 * fires from a board click too (`decisions/0188`: a Hidden enemy is on the board, so its token is the
 * natural pick); `outOfRangeTargetIds` dims every token {@link searchTargetReason} rejects, and the
 * Search card's `<select>` stays as the in-panel twin. **Stabilize, Mount, and Self-Destruct stay
 * excluded** — each collects its pick(s) in-panel (Stabilize: a
 * self-targeted primary + secondary, no board target at all; Mount, Jockey, and Ride: each an in-panel
 * UNIT pick filtered to a specific adjacent-mech candidate set (Mount: this pilot's own Unpiloted
 * mech; Jockey: an adjacent HOSTILE mech; Ride: an adjacent ALLIED mech),
 * `MountControls`/`JockeyControls`/`RideControls` — a mech is picked by id off a
 * `<select>`, never by clicking its board token; Self-Destruct: a self-targeted `at ∈ {1,2,3}` pick,
 * no board target at all — a stray token click arming an irreversible mech self-kill would be a
 * genuinely dangerous miss-click, so it stays excluded by the SAME implicit fall-through Stabilize
 * relies on, not a bespoke early return) behind a Fire/MOUNT/JOCKEY/RIDE/FIRE button, so a
 * canvas click while any is armed just selects/inspects, never fires a half-built action. **Release**
 * fires the same way as Grapple/Improvised Attack — no up-front declaration, any other combatant is a
 * legal click-to-fire target; the server re-checks fire-time range/legality, and an illegal release
 * is a rejected no-op that leaves the hold armed (there is no client-side dim mirror for it). Pure —
 * the click-to-target decision, unit-tested independently of the canvas.
 */
export function aimResolvesToFire(aim: ActionDraft | null, clickedId: string | null): boolean {
  if (aim === null || clickedId === null || clickedId === aim.attackerId) return false;
  // Barrage fires a board click only once a weapon card is armed; before that the click selects.
  if (aim.mode === "barrage") return aim.weaponId !== "";
  // Quick Tech fires a board click only once an option row is armed (mirrors Barrage's weapon-card
  // gate); before that the click selects (the in-panel chooser is still picking which of the four
  // options — Scan/Lock On/Invade/Bolster).
  if (aim.mode === "quick_tech") return aim.optionId !== "";
  // Full Tech mirrors Quick Tech's gate exactly, one pick at a time — an armed option row fires the
  // NEXT pick at the clicked token; the chooser re-arms after each pick.
  if (aim.mode === "full_tech") return aim.optionId !== "";
  // `activate_cell` (Smoke/HEX thrown to a point) has no "fire at this unit" concept — it only ever
  // commits a SITE via the stage's `onCommitCell`, so it's excluded here unconditionally. This
  // function is only even consulted once the stage's `onMouseDown` has already decided a token press
  // counts as a plain select (`CombatStage`'s `cellPickArmed`): an IN-RANGE occupied cell's press
  // stands down there entirely and
  // never reaches `onSelect`/this function at all — it falls straight through to `onCommitCell` at the
  // clicked cell, same as a bare cell. An OUT-of-range occupied cell's press still selects normally
  // (falls through here, which returns `false` for this mode), same as before this fix.
  //
  // `activate_sites` (directed multi-pick placement) has no "fire at this unit" concept either —
  // like `activate_cell` above, EVERY commit is a board CELL (accumulated via
  // `appendDirectedDeployPick`, never a token), so a token click while it's armed must fall through
  // to plain select. Stated as an EXPLICIT early return rather than left to the trailing `||`
  // chain's implicit exclusion, because a consumer that special-cases `activate_cell` by name and
  // does NOT inherit for a new mode is a regression this codebase has already shipped once
  // (`attackLosExcluded`).
  if (aim.mode === "activate_sites") return false;
  // Dismount/Eject have no "fire at this unit" concept either — like `activate_cell`/`activate_sites`
  // above, every commit is a board CELL (the pilot's landing space), never a token, so a token click
  // while either is armed must fall through to plain select. Explicit for the same reason
  // `activate_sites` is.
  if (aim.mode === "dismount" || aim.mode === "eject") return false;
  // Dismount (ally) / `ride_dismount` joins the SAME board-CELL family as Dismount/Eject above, for
  // the identical reason — the carrier is read
  // server-side off the rider's own `Riding.partner` (never picked here), but the landing space
  // is a board CELL, never a token, so a token click while it's armed must fall through to plain
  // select. Stated as its own EXPLICIT early return for the same reason the two above are explicit.
  if (aim.mode === "ride_dismount") return false;
  // **Prepare has no "fire at this unit" concept at all** — it commits from the `PrepareChooser`'s
  // own PREPARE button as an `encounter_command`, and the readied action's eventual target is
  // supplied later, at RELEASE (the `"release"` mode below is the half that DOES take a board
  // click). Stated as an EXPLICIT early return rather than left to the trailing `||` chain's
  // implicit exclusion, for the reason `activate_sites` above is explicit: a consumer that
  // special-cases modes by name and does not inherit for a new one is a regression this codebase
  // has already shipped once. Adding `prepare` to the chain below would make a stray token click
  // fire a half-built hold (`projects/prepare-hold-browser-arming.md` Gotchas).
  if (aim.mode === "prepare") return false;
  return (
    aim.mode === "skirmish" ||
    aim.mode === "activate" ||
    // System-granted tech attack — same one-click-fires
    // shape as `"activate"`: the row click already named the composite action id, so there's no
    // in-panel chooser step gating the fire (unlike Quick/Full Tech's option-armed gate above).
    aim.mode === "tech_attack" ||
    aim.mode === "ram" ||
    aim.mode === "grapple" ||
    aim.mode === "improvised_attack" ||
    aim.mode === "release" ||
    // Search: a click on a Hidden token in Sensors range searches it, the board twin of the
    // Search card's target select (`decisions/0188`: a Hidden enemy is on the board to click).
    aim.mode === "search" ||
    // A pilot's Mount, Jockey and Ride: the clicked adjacent mech, the same board twin of their
    // chooser cards (`pilot-command-menu` 2.4).
    aim.mode === "mount" ||
    aim.mode === "jockey" ||
    aim.mode === "ride"
  );
}

/**
 * Whether a **token press** while a cell-pick throw is armed (`activate_cell` — Smoke/HEX Charges)
 * should stand down instead of claiming the click for
 * select/drag — letting the trailing click fall through to the SAME bare-cell `onCommitCell` commit
 * an empty cell already reaches. `true` exactly when `cellPickArmed` and the pressed cell is inside
 * the action's reach (`attackRange.has(cellKey)`), or `attackRange` is `null` (the action declares no
 * reach ⇒ ungated, mirroring `onCommitCell`'s own out-of-range guard in `CombatApp.tsx`). An
 * out-of-range occupied cell keeps returning `false` — the press still selects normally, matching the
 * reach-wash convention elsewhere: an illegal target dims but a click on it still selects rather than
 * silently eating the click as a dead no-op. Pure — the press-routing decision, unit-tested
 * independently of the canvas (`CombatStage`'s `onMouseDown` is the one caller).
 */
export function cellPickBypassesSelect(cellPickArmed: boolean, attackRange: Set<string> | null, cellKey: string): boolean {
  if (!cellPickArmed) return false;
  return attackRange === null || attackRange.has(cellKey);
}

// ── Directed multi-pick placement — the accumulating draft's PURE transitions ──────────────────
//
// These are the ONLY way an `activate_sites` draft's `picks` change — arm fresh, append one, or pop
// the last one back off. Every transition returns a NEW draft (never mutates `draft`/`draft.picks`
// in place), matching every other piece of state this app threads through `setAim`. The callers are
// the board click-routing (illegal-cell routing via `cellPickBypassesSelect`, reusing the SAME
// predicate with the legal-cell wash standing in for `attackRange`) and the panel's back/cancel
// controls.

/** Narrow alias for the directed-placement draft variant — what every transition below reads/returns. */
type DirectedDeployDraft = Extract<ActionDraft, { mode: "activate_sites" }>;

/**
 * Arm a **fresh** directed-placement draft — `count`/`size` come straight from
 * `directedDeployRequirement` (`@lancer-vtt/rules`), the one place the pick shape is derived, so a
 * arming site calls this rather than hand-assembling the literal (the shape can't drift
 * between callers). `picks` always starts empty — there is no "resume a draft" concept; arming
 * always restarts the gesture from zero picks (matches every other `setAim` arm site, which always
 * mints a fresh draft, never continues a stale one).
 */
export function armDirectedDeployDraft(attackerId: CombatantId, actionId: string, requirement: DirectedDeployRequirement): DirectedDeployDraft {
  return {
    attackerId,
    mode: "activate_sites",
    actionId,
    count: requirement.count,
    size: requirement.size,
    ...(requirement.mine ? { mine: true } : {}),
    ...(requirement.placement ? { placement: requirement.placement } : {}),
    picks: [],
  };
}

/**
 * Append `cell` to `draft.picks` — a pure, immutable transition: a NEW draft with one more pick,
 * never a mutation of `draft`. A no-op (returns `draft` unchanged, same reference) once
 * {@link directedDeployDraftComplete} is already true — the click-routing caller is expected to
 * have already checked completeness/legality before calling, but this stays defensive
 * rather than silently over-filling `picks` past `count` if it's ever called out of order.
 */
export function appendDirectedDeployPick(draft: DirectedDeployDraft, cell: WireCell): DirectedDeployDraft {
  if (draft.picks.length >= draft.count) return draft;
  return { ...draft, picks: [...draft.picks, cell] };
}

/**
 * Pop the last pick off `draft.picks` — the "back" affordance (undo ONE pick, staying armed on
 * the same draft rather than cancelling the whole gesture). Free: nothing was ever sent for
 * a pick, so popping is a pure client-state discard, never an undo of a server command (there is no
 * `AttackLock`, no composite-undo entry, nothing server-side to unwind). A no-op (returns `draft`
 * unchanged, same reference) on an empty `picks` — the panel's own back control is expected to
 * hide/disable itself at zero picks, but this stays defensive rather than assuming the caller always
 * gates it first.
 */
export function popDirectedDeployPick(draft: DirectedDeployDraft): DirectedDeployDraft {
  if (draft.picks.length === 0) return draft;
  return { ...draft, picks: draft.picks.slice(0, -1) };
}

/**
 * Whether `draft` has accumulated every pick it needs (`picks.length >= count`) — the signal the
 * click-routing reads to decide "the next legal click appends AND fires the intent" vs. "the
 * next legal click just appends and stays armed." `>=` rather than `===` so a draft that somehow
 * over-filled (shouldn't happen — {@link appendDirectedDeployPick} refuses to grow past `count`)
 * still reads complete rather than perpetually "needs one more."
 */
export function directedDeployDraftComplete(draft: DirectedDeployDraft): boolean {
  return draft.picks.length >= draft.count;
}

/**
 * The **pending section marks** for a live directed multi-pick placement — which cells should carry
 * a translucent "a section goes here" mark right now, given whatever is currently armed. `null` =
 * paint nothing.
 *
 * **Why this exists.** The whole deploy is ONE atomic resolution: the picks never leave the client
 * until the gesture completes, so *nothing* exists in authoritative state until the final pick, and
 * without these marks both cover sections pop in at once with no acknowledgement of the first cell
 * the player clicked. The marks are a pure **render** affordance closing that visible gap — nothing
 * here is sent, stored, broadcast, or undoable.
 *
 * Reads {@link armDirectedDeployDraft}'s own accumulated `picks` — the SAME array
 * {@link appendDirectedDeployPick} grows and the final `request_activate` is eventually built from —
 * so a mark is by construction at a cell that will be sent, and the two cannot drift.
 *
 * Three deliberate `null` cases, all "there is no in-progress placement to acknowledge":
 * - nothing armed at all;
 * - something else armed (a Skirmish/Barrage/`activate_cell` aim is not a multi-pick placement);
 * - armed but **zero picks** — the gesture has started and the legal-cell wash is already up, but the
 *   player hasn't committed a cell yet, so there is nothing to acknowledge. (`null` rather than an
 *   empty list so the caller skips the work outright instead of drawing an empty layer.)
 *
 * A **complete** draft still returns its marks. That's intentional: completeness and teardown are
 * separate moments, and the caller drops the whole gesture (`aim` → `null`) in the same commit that
 * fires the intent, so the marks clear then. Blanking them here on the final pick would only add a
 * frame where the last-clicked cell reads as un-acknowledged.
 */
export function directedDeployPendingMarks(aim: ActionDraft | null): { cells: WireCell[]; size: number } | null {
  if (!aim || aim.mode !== "activate_sites" || aim.picks.length === 0) return null;
  return { cells: aim.picks, size: aim.size };
}

/**
 * One **per-cell targeting advisory** — whether the armed attacker
 * can actually land a shot on this reach-wash cell, and the cover degree it grants if so.
 */
export type CellSightline = {
  key: string;
  /** Whether any line from the attacker's footprint to this cell is unblocked. */
  visible: boolean;
  /** The cover degree the best line grants. */
  cover: Cover;
};

/**
 * The **line-of-sight exemption** an armed weapon's tags carry, or `null` when every wall stands.
 *
 * The two RAW tags (Core p.65 "Valid targets") both stand line of sight down and part ways on cover:
 * SEEKING "completely ignore[s] cover and line of sight"; ARCING "can be used to attack targets
 * without line of sight" but is "still affected by cover". SEEKING therefore outranks ARCING when a
 * weapon somehow carries both — it is the strictly stronger exemption, so nothing is lost by
 * reporting it.
 *
 * **Hoisted so the advisory and the words that explain it can never disagree.** {@link
 * armedCellSightlines} applies the exemption to the numbers the board paints; `describeCell`
 * (`routes/home/terrainCellDescription.ts`) writes the hovered-tile pane's sentence about *why* the
 * cover reads the way it does. A second copy of this tag test would be the exact drift
 * `terrain-legibility` D7 rules out for colours, applied to wording: the pane would say a shot
 * ignores cover while the hatch under the cursor still showed a degree, or the reverse.
 *
 * **The exemption is about the ADVISORY, never the geometry** — see {@link armedCellSightlines}.
 */
export function losExemptionFor(weapon: CoverWeapon | null): "seeking" | "arcing" | null {
  if (weapon?.tags?.some((t) => t.id === "tg_seeking") === true) return "seeking";
  if (weapon?.tags?.some((t) => t.id === "tg_arcing") === true) return "arcing";
  return null;
}

/** What {@link coverAppliesTo} and {@link losExemptionFor} read off a weapon: its reach and tags. */
export type CoverWeapon = Pick<WeaponProfile, "range" | "tags">;

/**
 * Whether cover changes this weapon's to-hit roll — the one predicate the armed wash and the
 * hover-intel rings both hatch by (`projects/range-preview-cover.md` F2). Mirrors the engine's
 * cover fold (`foldAttackCover`, `@lancer-vtt/rules`): ranged and not Seeking, or Thrown. A Blast
 * never hatches, because its cover is measured from the blast's center, not the shooter
 * (`decisions/0051` D4). A ranged-or-melee weapon reads as ranged, since its ring is its Range.
 * `null` (no weapon resolved, e.g. a granted action) keeps the hatch.
 */
export function coverAppliesTo(weapon: CoverWeapon | null): boolean {
  if (weapon === null) return true;
  const tagged = (id: string) => weapon.tags?.some((t) => t.id === id) === true;
  if (tagged("tg_seeking")) return false;
  if (weapon.range?.some((r) => r.kind === "blast") === true) return false;
  return isRangedAttackAt(weapon.range, null) || tagged("tg_thrown");
}

/**
 * The **armed unit's per-cell sightline set** — one {@link CellSightline}
 * per cell in the weapon's reach wash, LoS/cover computed straight from the attacker's footprint to
 * that cell (treated as a Size-1 footprint) via {@link terrainVisibility} — not per occupying
 * combatant. This is what makes "targetable" a **cell** property: an empty cell with a clear line
 * reports `visible: true` exactly like an occupied one would, so the on-canvas reach wash can show
 * only the cells a shot could actually land on, cover and all, before any enemy is even standing
 * there. `[]` when no reach wash is available (no weapon armed / attacker unplaced).
 *
 * **`weapon` carries the two RAW tag exemptions, and it is not optional decoration** — without it
 * this advisory hides squares the server would accept. `tg_arcing` and `tg_seeking` BOTH stand the
 * line-of-sight wall down (Core p.65 "Valid targets": ARCING "can be used to attack targets without
 * line of sight"; SEEKING "completely ignore cover and line of sight"), exactly as the server's own
 * `checkAttackCellLoS` (`@lancer-vtt/rules`) and this file's per-combatant twin
 * {@link attackTargetLosBlocked} already do. They part ways on **cover**: ARCING is "still affected
 * by cover", so its cells keep their computed degree and still draw the hatch, while SEEKING reports
 * `"none"` — mirroring the resolver's own skipped cover fold for Seeking
 * (`packages/rules/src/encounter/resolve/attackModifiers.ts`), so the advisory and the applied to-hit
 * math agree. `null`/omitted (no weapon resolved) is the un-exempt default: every wall stands.
 *
 * The exemption is applied to the ADVISORY, not to the geometry — `terrainVisibility` still runs per
 * cell, so an arcing shot's cover degree is the same number the to-hit fold will use. Only the
 * `visible` bit (and, for Seeking, the cover bit) is overridden, because those are the two RAW says
 * don't apply. Beyond Seeking, cover reads `"none"` wherever {@link coverAppliesTo} says the to-hit
 * fold skips it (melee, Blast), so those washes keep the clip and lose the hatch.
 *
 * The geometry is `sightlinesFrom` (`@lancer-vtt/domain`), which drops off-board cells and builds
 * the object and area cover sets once per call.
 */
export function armedCellSightlines(args: {
  state: EncounterState;
  attacker: Combatant;
  reachCells: ReachCell[] | null;
  /** The armed weapon, or a melee verb's bare reach (`meleeReachForAim`). */
  weapon: CoverWeapon | null;
  /**
   * The merged overlay. When given, a character that is hard cover for the occupant's allies (the
   * Goliath's Guardian) counts, via the SAME `alliedCoverCells` the attack's to-hit folds — so the
   * hatch matches the shot. Omitted, only terrain/objects/areas count.
   */
  overlay?: LoadedOverlay;
}): CellSightline[] {
  const { state, attacker, reachCells, weapon, overlay } = args;
  if (reachCells === null || !attacker.pos) return [];
  const ignoresLos = losExemptionFor(weapon) !== null;
  const showsCover = coverAppliesTo(weapon);
  const from = worldFor(state).footprint(attacker.pos, attacker.base.size).cells();
  return sightlinesFrom({
    state,
    from,
    cells: reachCells,
    ...(overlay !== undefined ? { extraHardCoverFor: (occupant: CombatantId) => alliedCoverCells({ state, targetId: occupant, attackerId: attacker.id, overlay }) } : {}),
  }).map(({ key, visible, cover }) => ({ key, visible: visible || ignoresLos, cover: showsCover ? cover : "none" }));
}

// ── Attack LoS target affordance ────────────────────────────────────────────────────────────
//
// `outOfRangeTargetIds` above already dims a Skirmish/Barrage target the reach wash can't touch;
// this is its LoS twin — the server's `checkAttackLoS`/`checkAttackCellLoS` (`economy.ts`)
// hard-reject a shot with no line of sight, so a walled-off target should read exactly like an
// out-of-range one: dimmed, click-withheld, no doomed shot sent. Widens the SAME
// `outOfRangeTargetIds` set rather than introducing a second dimming vocabulary.

/**
 * Whether the armed weapon is LoS-blocked from `attacker` to `target` — the COMBAT.app affordance
 * mirror of the server's `checkAttackLoS`/`checkAttackCellLoS` gate. Reuses the SAME
 * {@link terrainVisibility} geometry {@link armedCellSightlines} already calls (attacker footprint →
 * target footprint, `Cover`/edge-walls/placed-object folds included for free), and honors the
 * identical `tg_seeking`/`tg_arcing` exemption (RAW p.65: both ignore LoS — only Seeking additionally
 * ignores cover, which is irrelevant here) off the **already-resolved** armed weapon — `weapon` is the
 * `WeaponProfile` {@link weaponProfileForAim} (`attackRange.ts`) resolves, so this never re-derives the
 * weapon/profile lookup. An unplaced attacker or target reads as blocked (mirrors the server's own
 * can't-measure reject — `checkLineOfSight`/`checkAttackCellLoS` both hard-reject when either side
 * isn't placed). Pure, so it unit-tests without the component.
 */
export function attackTargetLosBlocked(args: { state: EncounterState; attacker: Combatant; target: Combatant; weapon: WeaponProfile | null }): boolean {
  const { state, attacker, target, weapon } = args;
  const isSeeking = weapon?.tags?.some((t) => t.id === "tg_seeking") ?? false;
  const isArcing = weapon?.tags?.some((t) => t.id === "tg_arcing") ?? false;
  if (isSeeking || isArcing) return false;
  if (!attacker.pos || !target.pos) return true;
  const space = worldFor(state);
  const from = space.footprint(attacker.pos, attacker.base.size).cells();
  const to = space.footprint(target.pos, target.base.size).cells();
  const sight = terrainVisibility({ space, terrain: state.terrain, from, to, edges: state.edges, combatants: state.combatants, excludeCoverFrom: target.id });
  return !sight.visible;
}

// ── Ram target affordance ─────────────────────────────────────────────────────
//
// Ram resolves through a synthetic **threat-1** melee weapon (`act_ram` in MELEE_ACTION_ATTACKS), so
// "adjacent" is exactly "within a threat-1 reach wash" — the same geometry the server's `checkAttackRange`
// gate enforces. The Size rule ("same Size or smaller") is the server's `checkRamSize` reject. The
// COMBAT.app Ram chooser **mirrors both as an affordance** (disables illegal target options) without
// re-implementing or re-enforcing them — the engine stays the wall (CLAUDE.md: "UI mirrors the reducer's
// rules as affordances; it never re-enforces them"), and a server rejection still surfaces inline if one slips through.

/** Ram's reach: the synthetic `act_ram` weapon is threat-1, so adjacency is a threat-1 reach wash. */
const RAM_REACH: WeaponRange[] = [{ kind: "threat", val: 1 }];

/** Why a candidate is an illegal Ram target (`null` when it's legal) — drives the board's dim/un-targetable gate. */
export type RamTargetReason = "unplaced" | "not_adjacent" | "too_large" | null;

/**
 * Whether `attacker` may Ram `target`, and if not, **why** — the COMBAT.app affordance mirror of the
 * server's two Ram gates. Reuses the SAME rule helpers the reducer uses: the threat-1 reach wash
 * ({@link attackReachCells}/{@link combatantInReach}, the UI twin of `checkAttackRange`) for adjacency,
 * and `checkRamSize` (`@lancer-vtt/rules`) for the "same Size or smaller" rule — never a re-implemented
 * rule. Pure, so it unit-tests without the component. The chooser disables a target whose reason is
 * non-null; the server is still the wall if an illegal one is forced through.
 */
export function ramTargetReason(state: EncounterState, attacker: Combatant, target: Combatant): RamTargetReason {
  if (!target.pos) return "unplaced";
  const reach = attackReachKeys(state, attacker, RAM_REACH);
  if (reach === null || !combatantInReachKeys(state, target, reach)) return "not_adjacent";
  if (!checkRamSize({ state, attacker: attacker.id, target: target.id }).ok) return "too_large";
  return null;
}

// ── Grapple target affordance ─────────────────────────────────────────────────
//
// Grapple resolves through the exact same synthetic **threat-1** melee-action-attack shape as Ram
// (`act_grapple` in MELEE_ACTION_ATTACKS) — a real melee attack vs Evasion, no weapon damage. Unlike
// Ram it carries **no Size gate** (RAW imposes none on Grapple — that rule is Ram-only), so
// adjacency is the ONLY affordance to mirror: the same threat-1 reach wash the server's
// `checkAttackRange` gate enforces.

/** Grapple's reach: the synthetic `act_grapple` weapon is threat-1, so adjacency is a threat-1 reach wash. */
const GRAPPLE_REACH: WeaponRange[] = [{ kind: "threat", val: 1 }];
/** Mount, Jockey and Ride's reach: the adjacent cells ({@link meleeReachForAim}). */
const PILOT_VERB_REACH: WeaponRange[] = [{ kind: "threat", val: 1 }];

/** Why a candidate is an illegal Grapple target (`null` when it's legal) — drives the board's dim/un-targetable gate. */
export type GrappleTargetReason = "unplaced" | "not_adjacent" | null;

/**
 * Whether `attacker` may Grapple `target`, and if not, **why** — the COMBAT.app affordance mirror of
 * the server's one Grapple gate (adjacency; no Size rule, unlike {@link ramTargetReason}). Reuses the
 * SAME threat-1 reach-wash helpers Ram's mirror does. Pure, so it unit-tests without the component.
 */
export function grappleTargetReason(state: EncounterState, attacker: Combatant, target: Combatant): GrappleTargetReason {
  if (!target.pos) return "unplaced";
  const reach = attackReachKeys(state, attacker, GRAPPLE_REACH);
  if (reach === null || !combatantInReachKeys(state, target, reach)) return "not_adjacent";
  return null;
}

// ── Improvised Attack target affordance (RAW Core p.71) ─────────────────────────────────────
//
// Improvised Attack resolves through the exact same synthetic **threat-1** melee-action-attack
// shape as Ram/Grapple (`act_improvised_attack` in MELEE_ACTION_ATTACKS) — a real melee attack
// vs Evasion, adjacency via the shared threat-1 reach wash. Unlike Ram it carries **no Size
// gate** (RAW imposes none), so adjacency is the ONLY affordance to mirror — same shape as
// Grapple's mirror, just a different synthetic weapon id.

/** Improvised Attack's reach: the synthetic `act_improvised_attack` weapon is threat-1. */
const IMPROVISED_ATTACK_REACH: WeaponRange[] = [{ kind: "threat", val: 1 }];

/** Why a candidate is an illegal Improvised Attack target (`null` when it's legal) — drives the board's dim/un-targetable gate. */
export type ImprovisedAttackTargetReason = "unplaced" | "not_adjacent" | null;

/**
 * Whether `attacker` may Improvised-Attack `target`, and if not, **why** — the COMBAT.app
 * affordance mirror of the server's one Improvised Attack gate (adjacency; no Size rule, unlike
 * {@link ramTargetReason}). Reuses the SAME threat-1 reach-wash helpers Ram/Grapple's mirrors do.
 * Pure, so it unit-tests without the component.
 */
export function improvisedAttackTargetReason(state: EncounterState, attacker: Combatant, target: Combatant): ImprovisedAttackTargetReason {
  if (!target.pos) return "unplaced";
  const reach = attackReachKeys(state, attacker, IMPROVISED_ATTACK_REACH);
  if (reach === null || !combatantInReachKeys(state, target, reach)) return "not_adjacent";
  return null;
}

/**
 * The reach a melee verb's aim washes (`token-command-menu` Phase 3): the SAME threat-1 reach its
 * target mirror above tests adjacency against (Ram, Grapple, Improvised Attack), so the red wash
 * and the rung targets agree by construction. `null` for every other mode.
 */
export function meleeReachForAim(aim: ActionDraft): WeaponRange[] | null {
  if (aim.mode === "ram") return RAM_REACH;
  if (aim.mode === "grapple") return GRAPPLE_REACH;
  if (aim.mode === "improvised_attack") return IMPROVISED_ATTACK_REACH;
  // A pilot's Mount, Jockey and Ride all need the mech adjacent (Core pp.70, 75).
  if (aim.mode === "mount" || aim.mode === "jockey" || aim.mode === "ride") return PILOT_VERB_REACH;
  return null;
}

// ── Search target affordance ──────────────────────────────────────────────────
//
// Search (`resolveSearch`, `packages/rules/src/encounter/search.ts` + `backend/src/encounterRoom.ts`)
// gates budget → the searcher's **Sensors range** (`checkTechRange`) → **line of sight**
// (`checkSearchLoS`) before drawing the RAW opposed contest. The COMBAT.app `SearchControls`
// **mirrors the Hidden + Sensors-range legality as a target FILTER** — a non-Hidden target is a
// legal no-op server-side but a
// pointless pick here, and an out-of-Sensors target would just hard-reject before any dice. LoS is
// NOT mirrored here (it needs the full `terrainVisibility` geometry the reach-wash overlay already
// carries for Skirmish/Barrage/Ram; Search has no board-click reach wash to hang it on) — a
// Sensors-in-range-but-blocked-LoS pick still surfaces the server's rejection via the toast, same as
// any other affordance gap. Never re-implements `checkTechRange`'s distance math — reuses the SAME
// `combatantRange` (`@lancer-vtt/rules`) footprint-to-footprint measure the gate itself uses.

/** Why a candidate is not a legal Search pick (`null` when it is) — drives the in-panel chooser's filter. */
export type SearchTargetReason = "unplaced" | "not_hidden" | "out_of_sensors" | null;

/**
 * Whether `attacker` may usefully Search `target`, and if not, **why** — the COMBAT.app affordance
 * mirror of Search's Hidden-target convention + its Sensors-range gate. `target` must carry
 * {@link HIDDEN_STATUS} (Search on a non-Hidden target is a legal engine no-op, but not a useful
 * board pick) and sit within the attacker's frozen `base.sensors` (measured via the SAME
 * `combatantRange` the server's `checkTechRange` gate uses — footprint-to-footprint, never
 * re-derived). No frozen Sensors ⇒ NOT gated (mirrors `checkTechRange`'s own stand-down for a base
 * predating the field). Pure, so it unit-tests without the component.
 */
export function searchTargetReason(state: EncounterState, attacker: Combatant, target: Combatant): SearchTargetReason {
  if (!hasStatus(target.statuses, HIDDEN_STATUS)) return "not_hidden";
  if (!attacker.pos || !target.pos) return "unplaced";
  const sensors = attacker.base.sensors;
  if (sensors === undefined) return null; // unknown Sensors ⇒ ungated, mirrors checkTechRange's stand-down
  const range = combatantRange(state, attacker.id, target.id);
  if (range === null) return "unplaced";
  if (range > sensors) return "out_of_sensors";
  return null;
}

// ── Mount target affordance (RAW Core p.70) ───────────────────────────────────
//
// Unlike every other target-affordance mirror in this file (Ram/Grapple/Improvised Attack/Search),
// Mount's ENTIRE legality is the wall — mech kind, Unpiloted, `partner` pointing at exactly this
// pilot, and adjacency (`checkMountLegality`, `@lancer-vtt/rules`) — so this is a bare pass-through
// to that predicate, not a new derived rule. `MountControls`'s candidate filter calls this per
// candidate; a non-null result means "don't offer this row," carrying the wall's own message rather
// than inventing a client-side reason vocabulary the way `RamTargetReason`/`SearchTargetReason` do.

/**
 * Whether `pilot` may Mount `mech`, and if not, **why** (the wall's own message, verbatim) — the
 * COMBAT.app affordance mirror of `checkMountLegality`. Calls the real exported predicate directly;
 * never re-derives any of its four conjuncts. Pure, so it unit-tests without the component.
 */
export function mountTargetReason(state: EncounterState, pilot: Combatant, mech: Combatant): string | null {
  const verdict = checkMountLegality(state, pilot.id, mech.id);
  return verdict.ok ? null : verdict.error;
}

// ── Jockey / Ride target affordance (RAW Core p.70/75) ────────────────────────
//
// Mount's own precedent, repeated twice: each of these two verbs' ENTIRE legality is its own wall
// (`checkJockeyLegality`/`checkRideLegality`, `@lancer-vtt/rules`) — a bare pass-through, never a
// re-derived rule. `JockeyControls`/`RideControls`'s candidate filters call the matching one per
// candidate; a non-null result means "don't offer this row."

/**
 * Whether `pilot` may Jockey `mech` (an adjacent HOSTILE mech), and if not, **why** — the
 * COMBAT.app affordance mirror of `checkJockeyLegality`. Calls the real exported predicate
 * directly; never re-derives the hostility/adjacency/already-jockeying conjuncts. Pure.
 */
export function jockeyTargetReason(state: EncounterState, pilot: Combatant, mech: Combatant): string | null {
  const verdict = checkJockeyLegality(state, pilot.id, mech.id);
  return verdict.ok ? null : verdict.error;
}

/**
 * Whether `pilot` may Ride `mech` (a willing, adjacent, SAME-SIDE mech), and if not, **why** — the
 * COMBAT.app affordance mirror of `checkRideLegality`. Calls the real exported predicate directly;
 * never re-derives the same-side/adjacency/already-riding conjuncts. Pure.
 */
export function rideTargetReason(state: EncounterState, pilot: Combatant, mech: Combatant): string | null {
  const verdict = checkRideLegality(state, pilot.id, mech.id);
  return verdict.ok ? null : verdict.error;
}

// ── Tech target affordance ────────────────────────────────────────────────────
//
// The four Quick Tech options {Scan, Lock On, Invade, Bolster} (`quickTechOption`,
// `@lancer-vtt/rules`) all read "choose a character within SENSORS" (RAW) — the click-to-target
// board mirror of the server's tech-target legality wall (`backend/src/encounterRoom.ts`). Reuses
// the SAME helpers those server gates use — never a forked
// rule: {@link combatantRange}/`attacker.base.sensors` (the `checkTechRange` Sensors measure,
// exactly like {@link searchTargetReason} above) and {@link immuneToTechAction} (the per-action
// tech-immunity predicate the chooser's `legalQuickTechOptions` already keys on). **No ally/enemy
// wall** — RAW puts no side restriction on any tech option ("Bolster an enemy"/"Scan an ally" is
// legal-but-dumb, not a gap); side is a client AFFORDANCE elsewhere, never gated here. The one
// side-aware reason is the willing-ally-Invade **deferral**: RAW lets you INVADE a willing
// allied character for an automatic no-roll, no-heat success, but that auto-success branch isn't
// implemented (no `resolveTechAttack`, no `willing` concept for tech) — resolving a same-side
// Invade through the standard tech-attack spine would WRONGLY roll d20+Tech vs the ally's
// E-Defense and deal it heat, so it's walled honestly (both here and server-side) rather than
// silently mis-resolved, until the auto-success branch lands.

/** Why a candidate is an illegal tech target (`null` when it's legal) — drives the board's dim/un-targetable gate. */
export type TechTargetReason = "out_of_sensors" | "tech_immune" | "self" | "willing_ally_invade_deferred" | null;

/** Args for {@link techTargetReason} (bundled — `CODE_STYLE.md`'s max-params-3 discipline). */
export type TechTargetArgs = {
  state: EncounterState;
  attacker: Combatant;
  /**
   * One of `quickTechOption`'s ids (`"scan" | "lock_on" | "invade" | "bolster"`) **or** a
   * system-granted tech-attack's composite `<itemId>:<actionIndex>` id (e.g. `"ms_markerlight:0"`,
   * a frozen `techAttackActions` key). See the function doc for how the two are told apart.
   */
  optionId: string;
  target: Combatant;
};

/**
 * Whether `attacker` may take Quick Tech option (or system-granted tech attack) `optionId` against
 * `target`, and if not, **why** — the COMBAT.app affordance mirror of the server's tech-target
 * legality wall. `quickTechOption(optionId)` resolves the four named Quick Tech ids; a composite
 * system-granted id (Markerlight) isn't one of those four, so `option` comes back
 * `undefined` and this function falls back to treating `optionId` itself as the tech-attack action
 * id — **not** "ungated": {@link immuneToTechAction} and the Sensors-range check below both still
 * run against it (they already take a bare action id, not a `quickTechOption` result), and the
 * same-side deferral below is now evaluated too (see its own comment for why that's required, not
 * optional, for a composite id). An option id that's neither a recognized Quick Tech option NOR one
 * of `attacker`'s frozen `techAttackActions` keys is a caller bug (the row/option list that produced
 * it should never offer one) — this function doesn't re-validate that, mirroring
 * `legalQuickTechOptions` being the source of truth for which options exist. Pure, so it
 * unit-tests without the component.
 */
export function techTargetReason(args: TechTargetArgs): TechTargetReason {
  const { state, attacker, optionId, target } = args;
  if (target.id === attacker.id) return "self";
  const pick = techMenuPick(optionId).option;
  const option = quickTechOption(pick);
  const actionId = option ? (option.route.kind === "tech_attack" ? option.route.weapon : option.route.action) : pick;
  if (immuneToTechAction({ state, targetId: target.id, actionId })) return "tech_immune";
  const sensors = attacker.base.sensors;
  if (sensors !== undefined) {
    const range = !attacker.pos || !target.pos ? null : combatantRange(state, attacker.id, target.id);
    if (range === null || range > sensors) return "out_of_sensors";
  }
  // Willing-ally Invade deferral — any TECH-ATTACK route, not just Invade: `refereeIntent.ts`'s
  // matching gate is keyed on `intent.tech` alone, unconditionally, for every tech attack including
  // a system-granted one like Markerlight. Gated only when BOTH sides carry a frozen `allegiance`,
  // mirroring the server wall (unknown side ⇒ not gated, never treated as "same").
  // `isTechAttackRoute` defaults `true` for a composite system-granted id (`option` is `undefined`
  // there — see this function's doc); defaulting it `false` would UNDER-gate the one case the
  // server does reject for a system-granted tech attack, a same-side target.
  const isTechAttackRoute = option ? option.route.kind === "tech_attack" : true;
  if (
    isTechAttackRoute &&
    attacker.base.allegiance !== undefined &&
    target.base.allegiance !== undefined &&
    sideOf(target) === sideOf(attacker)
  ) {
    return "willing_ally_invade_deferred";
  }
  return null;
}

// ── Sensors (TECH-RANGE) reach wash ───────────────────────────────────────────
//
// The board's Sensors-range overlay (`pixiCombatBoard.ts`'s `setSensorRange`, a dedicated layer
// mirroring `setMoveRange` — deliberately NOT folded into `setTargeting`, which owns
// weapon-red/cover-ring/AoE semantics). Gated on ANY Sensors-scoped aim. Range-only, mirroring the
// range-only `checkTechRange` wall; tech LoS is enforced server-side and deliberately NOT mirrored
// client-side (the same call Search makes) — a no-LoS pick hard-rejects server-side and surfaces
// via the toast.

/**
 * The aim modes whose reach is measured by the attacker's Sensors, not a weapon range. `tech_attack`
 * belongs here for the same reason `quick_tech`/`full_tech` do: RAW ties a tech attack's range to
 * Sensors (`checkTechRange`), never a weapon's `range[]` — a system-granted one (Markerlight) is no
 * exception.
 */
const SENSORS_SCOPED_AIM_MODES: ReadonlySet<ActionDraft["mode"]> = new Set(["search", "quick_tech", "full_tech", "tech_attack"]);

/**
 * The Sensors reach-wash cell keys for the currently armed `aim`, or `null` when nothing
 * Sensors-scoped is armed / the attacker is gone / unplaced / has no frozen Sensors. Computed with
 * ZERO new geometry — {@link attackReachKeys} (built on {@link attackReachCellList}) fed a synthetic
 * `{ kind: "range", val: attacker.base.sensors }`, the SAME reach-cell builder the weapon wash uses.
 * Pure, so it unit-tests without the component.
 */
export function sensorReachCells(state: EncounterState, aim: ActionDraft | null): Set<string> | null {
  if (!aim || !SENSORS_SCOPED_AIM_MODES.has(aim.mode)) return null;
  const attacker = state.combatants[aim.attackerId];
  if (!attacker) return null;
  const sensors = attacker.base.sensors;
  if (sensors === undefined) return null;
  return attackReachKeys(state, attacker, [{ kind: "range", val: sensors }]);
}
